/**
 * /admin/matches/:matchId/incidents/ — unified Law-41/42 timeline.
 *
 * Pairs with backend Batch 7 (commit e35fa6d):
 *   GET /api/matches/<match_id>/incidents/[?type=<csv>]
 *
 * Replaces the SPA's prior "five separate fetches" pattern in
 * MatchControl with a single chronologically-ordered stream of
 * substitutions + warnings + penalties + conduct reports + ball edits
 * + Law-41/42 dismissals. The press-report generator (and any future
 * incident-timeline component) can read from the same endpoint.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  RefreshCw, ArrowLeft, ArrowRightLeft, AlertOctagon, Coins,
  Gavel, Pencil, Skull, AlertTriangle, Filter,
} from "lucide-react";
import api from "../../api/axios";

const TYPE_META = {
  substitution:           { label: "Substitution", color: "text-amber-300",   Icon: ArrowRightLeft },
  warning:                { label: "Warning",      color: "text-amber-300",   Icon: AlertOctagon },
  penalty:                { label: "Penalty runs", color: "text-rose-300",    Icon: Coins },
  conduct:                { label: "Conduct",      color: "text-rose-300",    Icon: Gavel },
  ball_edit:              { label: "Ball edit",    color: "text-text-secondary", Icon: Pencil },
  dismissal_law_41_42:    { label: "Law 41/42 dismissal", color: "text-rose-200", Icon: Skull },
};

const TYPE_KEYS = Object.keys(TYPE_META);

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export default function AdminMatchIncidents() {
  const { matchId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enabledTypes, setEnabledTypes] = useState(new Set(TYPE_KEYS));

  async function load() {
    setLoading(true);
    setError(null);
    try {
      // The backend supports a ?type= CSV filter, but for the typical
      // 50-event match we just fetch everything and filter client-side
      // so the operator can toggle visibility without re-fetching.
      const { data: payload } = await api.get(
        `/api/matches/${matchId}/incidents/`,
      );
      setData(payload);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [matchId]);

  const filtered = useMemo(() => {
    if (!data?.incidents) return [];
    return data.incidents.filter((x) => enabledTypes.has(x.type));
  }, [data, enabledTypes]);

  function toggleType(t) {
    setEnabledTypes((s) => {
      const next = new Set(s);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  // Per-type counts (across all incidents, not just filtered) so the
  // pill labels reflect what's available.
  const counts = useMemo(() => {
    const m = {};
    for (const t of TYPE_KEYS) m[t] = 0;
    for (const inc of data?.incidents || []) {
      if (m[inc.type] != null) m[inc.type] += 1;
    }
    return m;
  }, [data]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <Link to={`/cricket/matches/${matchId}/control`} className="hover:underline flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Match control
            </Link>
          </div>
          <h1 className="text-2xl font-semibold mt-1">Match incidents timeline</h1>
          <p className="text-sm text-text-secondary mt-1">
            Substitutions, warnings, penalties, conduct reports, ball
            edits, and Law 41/42 dismissals merged chronologically. The
            single source of truth that feeds the press report.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-sm hover:bg-bg-secondary"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </header>

      {error && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 p-3 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {data?.truncated && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 p-3 text-sm">
          Showing first {data.max_incidents} incidents; older entries
          truncated. Use the type filter to narrow further.
        </div>
      )}

      {/* Type filter pills */}
      {data && (
        <div className="flex flex-wrap gap-2 items-center">
          <Filter className="w-3.5 h-3.5 text-text-secondary" />
          {TYPE_KEYS.map((t) => {
            const meta = TYPE_META[t];
            const active = enabledTypes.has(t);
            const Icon = meta.Icon;
            return (
              <button
                key={t}
                onClick={() => toggleType(t)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? "bg-bg-primary border-border-default text-text-primary"
                    : "bg-bg-primary border-border-default text-text-secondary opacity-50"
                }`}
              >
                <Icon className={`w-3 h-3 ${meta.color}`} />
                {meta.label}
                <span className="ml-1 font-mono">{counts[t] || 0}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Timeline */}
      {loading && !data && (
        <div className="rounded-lg border border-border-default bg-bg-primary p-12 text-center text-sm text-text-secondary">
          Loading timeline…
        </div>
      )}

      {data && filtered.length === 0 && (
        <div className="rounded-lg border border-border-default bg-bg-primary p-8 text-center text-sm text-text-secondary">
          {(data.incidents || []).length === 0
            ? "No Law-41/42 events recorded for this match yet."
            : "No incidents match the filter — toggle a type above."}
        </div>
      )}

      {filtered.length > 0 && (
        <ol className="relative space-y-3">
          {filtered.map((inc, idx) => {
            const meta = TYPE_META[inc.type] || TYPE_META.ball_edit;
            const Icon = meta.Icon;
            const overLabel = (inc.innings_number != null && inc.over_number != null)
              ? `Inn ${inc.innings_number} · ${inc.over_number}.${inc.ball_in_over}`
              : null;
            return (
              <li
                key={`${inc.type}-${inc.id}-${idx}`}
                className="rounded-lg border border-border-default bg-bg-primary p-3 flex items-start gap-3"
              >
                <div className={`mt-0.5 ${meta.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold ${meta.color}`}>
                      {meta.label}
                    </span>
                    {overLabel && (
                      <span className="text-[11px] font-mono text-text-secondary">
                        {overLabel}
                      </span>
                    )}
                    <span className="text-[11px] text-text-secondary ml-auto tabular-nums">
                      {fmt(inc.timestamp)}
                    </span>
                  </div>
                  <div className="text-sm mt-1">{inc.summary}</div>
                  {inc.detail && Object.keys(inc.detail).length > 0 && (
                    <details className="text-[11px] text-text-secondary mt-1.5">
                      <summary className="cursor-pointer hover:text-text-primary">
                        Detail
                      </summary>
                      <pre className="mt-1 font-mono whitespace-pre-wrap rounded border border-border-default bg-bg-secondary/40 p-2 max-h-60 overflow-auto">
                        {JSON.stringify(inc.detail, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {data && (
        <div className="text-xs text-text-secondary text-right">
          Showing {filtered.length} of {data.count} incidents
        </div>
      )}
    </div>
  );
}
